import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import { palette } from '../../material-ui/palette';
import { Trans, useTranslation } from 'react-i18next';
const PREFIX = 'OpenDoorDialog';

const classes = {
    idMatchingContainer: `${PREFIX}-idMatchingContainer`,
    dialogTitle: `${PREFIX}-dialogTitle`,
    dialogContent: `${PREFIX}-dialogContent`,
    openDoorButton: `${PREFIX}-confirmButton`,
    cancelButton: `${PREFIX}-cancelButton`,
    backdropOpacity: `${PREFIX}-backdropOpacity`,
};


const StyledDialog = styled(Dialog)((
    {
        theme,
    },
) => ({
    [`& .${classes.idMatchingContainer}`]: {
        opacity: '0.8',
        margin: `${theme.spacing(2)} 0`,
        display: 'flex',
        '& > *': {
            flex: 1,
            '&:not(:last-child)': {
                marginRight: theme.spacing(1),
                [theme.breakpoints.down('sm')]: {
                    marginRight: 0,
                    marginBottom: theme.spacing(2),
                },
            },
        },
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },

    [`& .${classes.dialogTitle}`]: {
        textTransform: 'none',
        fontSize: '16px',
        lineHeight: '20px',
        fontWeight: '600',
        color: palette.text.primary,
    },


    [`& .${classes.dialogContent}`]: {
        textTransform: 'none',
        fontSize: '16px',
        lineHeight: '20px',
        fontWeight: '500',
        color: palette.text.primary,
    },

    [`& .${classes.openDoorButton}`]: {
        height: '44px',
        padding: '12px',
        textTransform: 'none',
        fontSize: '16px',
        fontStyle: 'normal',
        lineHeight: '20px',
        letterSpacing: '0.32px',
        fontWeight: '600',
        borderRadius: '12px',
    },

    [`& .${classes.cancelButton}`]: {
        color: 'black',
        height: '44px',
        padding: '12px',
        textTransform: 'none',
        fontSize: '16px',
        fontStyle: 'normal',
        lineHeight: '20px',
        letterSpacing: '0.32px',
        fontWeight: '600',
        border: '1px solid black',
        backgroundColor: 'white',
        borderRadius: '12px',

    },

    [`& .${classes.backdropOpacity}`]: {
        backgroundColor: alpha(palette.primary.main, 0.8),

    },
}));


interface LockOrUnlockDialogProps {
    action: 'block' | 'unblock';
    open: boolean;
    doorNumber: number;
    depotName: string;
    // The locker is blocked while a customer is still out with it. They keep
    // it until their booked time is up — only the reallocation afterwards is
    // prevented. Says so explicitly, because "blocked" otherwise reads as if
    // the running booking were cut short.
    blocksRunningRental?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}


const LockOrUnlockDialog = (props: LockOrUnlockDialogProps) => {
    const { t } = useTranslation();

    const textKey = (() => {
        if (props.action === 'unblock') {
            return 'depot_details.block_or_unblock_warning.text.unblock';
        }
        return props.blocksRunningRental
            ? 'depot_details.block_or_unblock_warning.text.block_in_use'
            : 'depot_details.block_or_unblock_warning.text.block';
    })();

    return (
        <StyledDialog
            slotProps={{ backdrop: { className: classes.backdropOpacity } }}
            open={props.open}
            aria-labelledby={'alert-dialog-title'}
            aria-describedby={'alert-dialog-description'}
        >
            <DialogTitle className={`${classes.dialogTitle}`}>
                {
                    props.action === 'block' 
                        ? 
                        t('depot_details.block_or_unblock_warning.title.block', { doorNumber: props.doorNumber })
                        :
                        t('depot_details.block_or_unblock_warning.title.unblock', { doorNumber: props.doorNumber })
                }
            </DialogTitle>
            <DialogContent>
                <DialogContentText
                    id={'alert-dialog-description'}
                    className={`${classes.dialogContent}`}
                >
                    <Trans
                        i18nKey={textKey}
                        values={{ doorNumber: props.doorNumber, depotName: props.depotName }}
                        components={{ strong: <strong /> } as any}
                    />
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button className={`${classes.cancelButton}`} onClick={props.onCancel}>
                    {t('common.cancel')}
                </Button>
                <Button
                    className={classes.openDoorButton}
                    onClick={props.onConfirm}
                    variant={'contained'}
                >
                    {t('common.confirm')}
                </Button>
            </DialogActions>
        </StyledDialog>
    );
};

export default LockOrUnlockDialog;
